/* Ardor Sticker Builder: print template PDF + finished-template reader (Oct 12, 2026, TPL-STICKER). Ardor Printing.
   window.ArdorStickerTemplate.build(spec) -> Promise<Blob>: an Ardor-branded PDF template for the size and shape picked in
   the builder. The page is the exact print size (cut size + 1/16" bleed on every side). Everything drawn on it (bleed
   area, cut line, dashed safe line, labels, short instructions, Ardor logo + ardorprinting.com) sits on ONE PDF layer
   (optional content group) named "Ardor guides - hide before saving", so customers can hide it before they save.
   .readPdf(file, pageW, pageH) -> Promise<{url, w, h} | null>: when page 1 of an uploaded PDF matches the template page
   (inches, within 1%), renders it with the guides layer hidden, for the builder's "Template file detected" auto-fit.
   spec = {shape: square|rounded|circle|contour|kiss, w, h (cut size, in), bleed, border, kissMargin, radiusFrac} */
(function () {
'use strict';
if (window.ArdorStickerTemplate) return;

var me = document.currentScript;
var CONF = {
  logo: (me && me.getAttribute('data-logo')) || '',
  pdfjs: (me && me.getAttribute('data-pdfjs')) || '',
  worker: (me && me.getAttribute('data-pdfjs-worker')) || ''
};
var LAYER = 'Ardor guides - hide before saving';
var SAFE = 0.125;
var K = 72;
var KAPPA = 0.5522847498;
var RED = [0.741, 0.145, 0.173], BLUE = [0, 0.46, 0.84], INK = [0.1, 0.1, 0.11], GREY = [0.36, 0.36, 0.38];

function r3(v) { return Math.round(v * 1000) / 1000; }
function n(v) { var s = (Math.round(v * 1000) / 1000).toFixed(3); s = s.replace(/\.?0+$/, ''); return s === '-0' ? '0' : s; }
function inch(v) { return n(r3(v)); }
function frac(v) {
  var m = { '0.0625': '1/16', '0.09375': '3/32', '0.125': '1/8', '0.15625': '5/32', '0.1875': '3/16', '0.25': '1/4' };
  return m[String(v)] || inch(v);
}
function isRect(shape) { return shape === 'square' || shape === 'rounded' || shape === 'circle'; }
function shapeName(sp) {
  var same = Math.abs(sp.w - sp.h) < 0.001;
  switch (sp.shape) {
    case 'square': return same ? 'Square' : 'Rectangle';
    case 'rounded': return 'Rounded Corner';
    case 'circle': return same ? 'Circle' : 'Oval';
    case 'kiss': return 'Kiss-Cut';
    default: return 'Die-Cut';
  }
}
function fileName(sp) {
  return 'Ardor-Template-Sticker-' + shapeName(sp).replace(/ /g, '-') + '-' + inch(sp.w) + 'x' + inch(sp.h) + 'in-Front.pdf';
}

function geometry(sp) {
  var b = sp.bleed || 0.0625, w = sp.w, h = sp.h, pw = w + 2 * b, ph = h + 2 * b;
  var g = { b: b, w: w, h: h, pw: pw, ph: ph, PW: pw * K, PH: ph * K, rect: isRect(sp.shape), shape: sp.shape };
  g.cut = { x: b * K, y: b * K, w: w * K, h: h * K, r: 0 };
  var inset;
  if (g.rect) {
    g.cutKind = sp.shape === 'circle' ? 'ellipse' : 'rrect';
    if (sp.shape === 'rounded') g.cut.r = Math.min(w, h) * (sp.radiusFrac || 0.06) * K;
    inset = SAFE;
  } else {
    g.cutKind = 'box';
    inset = (sp.border || 0.125) + (sp.shape === 'kiss' ? (sp.kissMargin || 0.125) : 0);
  }
  g.inset = Math.min(inset, Math.min(w, h) / 2 - 0.05);
  var s = g.inset * K;
  g.safe = { x: g.cut.x + s, y: g.cut.y + s, w: Math.max(2, g.cut.w - 2 * s), h: Math.max(2, g.cut.h - 2 * s), r: Math.max(0, g.cut.r - s) };
  return g;
}

/* ---------- path helpers (PDF user space, y up; all shapes are symmetric so the flip does not matter) ---------- */
function rrectPath(R) {
  var x = R.x, y = R.y, w = R.w, h = R.h, r = Math.min(R.r || 0, w / 2, h / 2);
  if (r <= 0.01) return n(x) + ' ' + n(y) + ' ' + n(w) + ' ' + n(h) + ' re\n';
  var c = r * (1 - KAPPA), x2 = x + w, y2 = y + h;
  return [
    n(x + r) + ' ' + n(y) + ' m',
    n(x2 - r) + ' ' + n(y) + ' l',
    n(x2 - c) + ' ' + n(y) + ' ' + n(x2) + ' ' + n(y + c) + ' ' + n(x2) + ' ' + n(y + r) + ' c',
    n(x2) + ' ' + n(y2 - r) + ' l',
    n(x2) + ' ' + n(y2 - c) + ' ' + n(x2 - c) + ' ' + n(y2) + ' ' + n(x2 - r) + ' ' + n(y2) + ' c',
    n(x + r) + ' ' + n(y2) + ' l',
    n(x + c) + ' ' + n(y2) + ' ' + n(x) + ' ' + n(y2 - c) + ' ' + n(x) + ' ' + n(y2 - r) + ' c',
    n(x) + ' ' + n(y + r) + ' l',
    n(x) + ' ' + n(y + c) + ' ' + n(x + c) + ' ' + n(y) + ' ' + n(x + r) + ' ' + n(y) + ' c',
    'h'
  ].join('\n') + '\n';
}
function ellipsePath(R) {
  var cx = R.x + R.w / 2, cy = R.y + R.h / 2, rx = R.w / 2, ry = R.h / 2, ox = rx * KAPPA, oy = ry * KAPPA;
  return [
    n(cx + rx) + ' ' + n(cy) + ' m',
    n(cx + rx) + ' ' + n(cy + oy) + ' ' + n(cx + ox) + ' ' + n(cy + ry) + ' ' + n(cx) + ' ' + n(cy + ry) + ' c',
    n(cx - ox) + ' ' + n(cy + ry) + ' ' + n(cx - rx) + ' ' + n(cy + oy) + ' ' + n(cx - rx) + ' ' + n(cy) + ' c',
    n(cx - rx) + ' ' + n(cy - oy) + ' ' + n(cx - ox) + ' ' + n(cy - ry) + ' ' + n(cx) + ' ' + n(cy - ry) + ' c',
    n(cx + ox) + ' ' + n(cy - ry) + ' ' + n(cx + rx) + ' ' + n(cy - oy) + ' ' + n(cx + rx) + ' ' + n(cy) + ' c',
    'h'
  ].join('\n') + '\n';
}
function shapePath(g, R) { return g.cutKind === 'ellipse' ? ellipsePath(R) : rrectPath(R); }
function rgb(c, stroke) { return n(c[0]) + ' ' + n(c[1]) + ' ' + n(c[2]) + (stroke ? ' RG\n' : ' rg\n'); }

/* ---------- text ---------- */
var mctx = null;
function textW(t, bold, size) {
  if (!mctx) { var cv = document.createElement('canvas'); mctx = cv.getContext('2d'); }
  mctx.font = (bold ? 'bold ' : '') + '100px Helvetica, Arial, sans-serif';
  return mctx.measureText(t).width / 100 * size;
}
function pdfStr(t) {
  return '(' + String(t).replace(/[^\x20-\x7e]/g, '').replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)') + ')';
}
function wrap(t, bold, size, maxW) {
  var words = String(t).split(' '), out = [], cur = '';
  for (var i = 0; i < words.length; i++) {
    var tryS = cur ? cur + ' ' + words[i] : words[i];
    if (cur && textW(tryS, bold, size) > maxW) { out.push(cur); cur = words[i]; } else cur = tryS;
  }
  if (cur) out.push(cur);
  return out;
}

function notes(sp, g) {
  var W = inch(sp.w), H = inch(sp.h), page = inch(g.pw) + '" x ' + inch(g.ph) + '"', L = [];
  var name = shapeName(sp).toLowerCase();
  if (g.rect) {
    L.push({ p: 1, t: 'FRONT - ' + W + '" x ' + H + '" ' + name + ' sticker', bold: true, k: 1.18 });
    L.push({ p: 3, t: 'Page ' + page + ': cut size + 1/16" bleed on every side' });
    L.push({ p: 1, t: 'Cut line (the edge of your sticker)', key: 'cut' });
    L.push({ p: 1, t: 'Safe line: keep words and logos inside it', key: 'safe' });
    L.push({ p: 1, t: 'Bleed: extend the background to the outer edge', key: 'bleed' });
  } else {
    var kiss = sp.shape === 'kiss';
    L.push({ p: 1, t: 'FRONT - ' + W + '" x ' + H + '" ' + name + ' sticker' + (kiss ? ' (backing size)' : ' (largest size)'), bold: true, k: 1.18 });
    L.push({ p: 3, t: 'Page ' + page + ' with 1/16" bleed on every side' });
    L.push({ p: 1, t: kiss ? 'Backing sheet edge' : 'Sticker size: the cut line stays inside this box', key: 'box' });
    L.push({ p: 1, t: 'Keep all artwork inside the dashed line', key: 'safe' });
    L.push({ p: 2, t: kiss
      ? 'We kiss-cut ' + frac(sp.border || 0.125) + '" around your artwork. The backing goes 1/8" past the cut.'
      : 'We cut ' + frac(sp.border || 0.125) + '" around the outline of your artwork and add the bleed for you.' });
    L.push({ p: 2, t: 'Use a transparent background so the cut follows your design.' });
  }
  L.push({ p: 1, t: 'Hide this "' + LAYER + '" layer before you save.' });
  L.push({ p: 2, t: 'Save the whole page as a PDF, or as a PNG or JPG at 300 dpi.' });
  L.push({ p: 3, t: 'Upload it in our sticker builder. It snaps to this size.' });
  return L;
}

function layout(sp, g, logo) {
  var area = g.cutKind === 'ellipse'
    ? { x: g.safe.x + g.safe.w * 0.1464, y: g.safe.y + g.safe.h * 0.1464, w: g.safe.w * 0.7071, h: g.safe.h * 0.7071 }
    : { x: g.safe.x, y: g.safe.y, w: g.safe.w, h: g.safe.h };
  var pad = Math.min(area.w, area.h) * 0.05;
  area = { x: area.x + pad, y: area.y + pad, w: Math.max(4, area.w - 2 * pad), h: Math.max(4, area.h - 2 * pad) };
  var all = notes(sp, g), minDim = Math.min(area.w, area.h);
  var fsMax = Math.max(2.5, Math.min(10, minDim / 16)), fsMin = 2.2;
  function build(maxP, fs) {
    var rows = [], lead = 1.28, widest = 0;
    all.forEach(function (ln) {
      if (ln.p > maxP) return;
      var size = fs * (ln.k || 1), sample = ln.key ? size * 2.4 : 0, avail = area.w - sample;
      wrap(ln.t, !!ln.bold, size, avail).forEach(function (txt, i) {
        var tw = textW(txt, !!ln.bold, size);
        rows.push({ txt: txt, bold: !!ln.bold, size: size, key: i === 0 ? ln.key : null, sample: sample, w: tw + sample, h: size * lead });
        widest = Math.max(widest, tw + sample);
      });
    });
    var logoH = logo ? fs * 2.2 : 0, logoW = logo ? logoH * logo.w / logo.h : 0;
    var urlSize = fs * 0.95;
    rows.push({ gap: fs * 0.5 });
    if (logo) rows.push({ logo: true, w: logoW, h: logoH + fs * 0.25 });
    rows.push({ txt: 'ardorprinting.com', bold: true, size: urlSize, w: textW('ardorprinting.com', true, urlSize), h: urlSize * lead, color: RED });
    var total = 0;
    rows.forEach(function (r) { total += r.gap || r.h; widest = Math.max(widest, r.w || 0); });
    return { rows: rows, total: total, widest: widest, fs: fs };
  }
  var best = null;
  for (var maxP = 3; maxP >= 1 && !best; maxP--) {
    for (var fs = fsMax; fs >= fsMin - 1e-6; fs -= 0.25) {
      var b = build(maxP, fs);
      if (b.total <= area.h && b.widest <= area.w + 0.5) { best = b; break; }
    }
  }
  if (!best) best = build(1, fsMin);
  best.area = area;
  return best;
}

function content(sp, g, logo) {
  var c = '/OC /ArdorGuides BDC\n';
  // bleed zone (between the page edge and the cut line / size box), light tint
  c += 'q /GSTint gs ' + rgb(RED) + '0 0 ' + n(g.PW) + ' ' + n(g.PH) + ' re\n' + (g.rect ? shapePath(g, g.cut) : rrectPath(g.cut)) + 'f*\nQ\n';
  // cut line or size box
  var lw = Math.max(0.5, Math.min(1.25, Math.min(g.PW, g.PH) / 220));
  if (g.rect) c += 'q ' + rgb(RED, true) + n(lw) + ' w ' + shapePath(g, g.cut) + 'S\nQ\n';
  else c += 'q ' + rgb(GREY, true) + n(lw * 0.8) + ' w ' + rrectPath(g.cut) + 'S\nQ\n';
  // dashed safe line / artwork area
  var dash = Math.max(1.5, lw * 4);
  c += 'q ' + rgb(BLUE, true) + n(lw * 0.8) + ' w [' + n(dash) + ' ' + n(dash * 0.7) + '] 0 d ' + (g.rect ? shapePath(g, g.safe) : rrectPath(g.safe)) + 'S\nQ\n';
  // notes panel
  var L = layout(sp, g, logo), area = L.area, padP = L.fs * 0.7;
  var bw = Math.min(area.w + padP * 2, L.widest + padP * 2), bh = L.total + padP * 2;
  var bx = area.x + area.w / 2 - bw / 2, by = area.y + area.h / 2 - bh / 2;
  c += 'q /GSPanel gs 1 1 1 rg ' + rrectPath({ x: bx, y: by, w: bw, h: bh, r: L.fs * 0.6 }) + 'f\nQ\n';
  var y = by + bh - padP, cx = area.x + area.w / 2;
  L.rows.forEach(function (r) {
    if (r.gap) { y -= r.gap; return; }
    if (r.logo) {
      var lh = r.h - L.fs * 0.25;
      c += 'q ' + n(r.w) + ' 0 0 ' + n(lh) + ' ' + n(cx - r.w / 2) + ' ' + n(y - lh) + ' cm /ArdorLogo Do Q\n';
      y -= r.h; return;
    }
    var x0 = cx - r.w / 2, base = y - r.size * 0.95;
    if (r.key) {
      var sx = x0, sw = r.sample * 0.8, my = base + r.size * 0.33;
      if (r.key === 'cut') c += 'q ' + rgb(RED, true) + n(Math.max(0.6, r.size * 0.14)) + ' w ' + n(sx) + ' ' + n(my) + ' m ' + n(sx + sw) + ' ' + n(my) + ' l S Q\n';
      else if (r.key === 'box') c += 'q ' + rgb(GREY, true) + n(Math.max(0.5, r.size * 0.1)) + ' w ' + n(sx) + ' ' + n(my) + ' m ' + n(sx + sw) + ' ' + n(my) + ' l S Q\n';
      else if (r.key === 'safe') c += 'q ' + rgb(BLUE, true) + n(Math.max(0.5, r.size * 0.12)) + ' w [' + n(r.size * 0.35) + ' ' + n(r.size * 0.25) + '] 0 d ' + n(sx) + ' ' + n(my) + ' m ' + n(sx + sw) + ' ' + n(my) + ' l S Q\n';
      else if (r.key === 'bleed') c += 'q /GSSwatch gs ' + rgb(RED) + n(sx) + ' ' + n(base - r.size * 0.05) + ' ' + n(sw) + ' ' + n(r.size * 0.8) + ' re f Q\n';
      x0 += r.sample;
    }
    c += 'BT /' + (r.bold ? 'F2' : 'F1') + ' ' + n(r.size) + ' Tf ' + rgb(r.color || INK) + n(x0) + ' ' + n(base) + ' Td ' + pdfStr(r.txt) + ' Tj ET\n';
    y -= r.h;
  });
  c += 'EMC\n';
  return c;
}

/* ---------- logo: PNG -> RGB + alpha (SMask), both Flate ---------- */
var logoCache = null;
function deflate(bytes) {
  if (typeof CompressionStream === 'undefined') return Promise.resolve(null);
  var cs = new CompressionStream('deflate');
  var out = new Blob([bytes]).stream().pipeThrough(cs);
  return new Response(out).arrayBuffer().then(function (b) { return new Uint8Array(b); });
}
function loadLogo() {
  if (logoCache) return logoCache;
  logoCache = (!CONF.logo ? Promise.resolve(null) : fetch(CONF.logo, { credentials: 'omit' }).then(function (r) {
    if (!r.ok) throw new Error('logo ' + r.status);
    return r.blob();
  }).then(function (b) { return createImageBitmap(b); }).then(function (bm) {
    var s = Math.min(1, 640 / bm.width), w = Math.max(1, Math.round(bm.width * s)), h = Math.max(1, Math.round(bm.height * s));
    var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
    var cx = cv.getContext('2d'); cx.drawImage(bm, 0, 0, w, h);
    var d = cx.getImageData(0, 0, w, h).data, rgbA = new Uint8Array(w * h * 3), a = new Uint8Array(w * h), hasA = false;
    for (var i = 0, j = 0; i < w * h; i++, j += 4) { rgbA[i * 3] = d[j]; rgbA[i * 3 + 1] = d[j + 1]; rgbA[i * 3 + 2] = d[j + 2]; a[i] = d[j + 3]; if (d[j + 3] < 255) hasA = true; }
    return Promise.all([deflate(rgbA), hasA ? deflate(a) : null]).then(function (z) {
      if (!z[0]) return null;
      return { w: w, h: h, rgb: z[0], alpha: z[1] };
    });
  }).catch(function () { logoCache = null; return null; }));
  return logoCache;
}

/* ---------- PDF writer ---------- */
function enc(s) { var u = new Uint8Array(s.length); for (var i = 0; i < s.length; i++) u[i] = s.charCodeAt(i) & 255; return u; }
function writePdf(objs) {
  // objs[i] = string or {dict, data:Uint8Array}; object number = i + 1
  var parts = [], offs = [], pos = 0;
  function push(u) { parts.push(u); pos += u.length; }
  push(enc('%PDF-1.6\n%\xE2\xE3\xCF\xD3\n'));
  objs.forEach(function (o, i) {
    offs[i] = pos;
    if (typeof o === 'string') push(enc((i + 1) + ' 0 obj\n' + o + '\nendobj\n'));
    else { push(enc((i + 1) + ' 0 obj\n' + o.dict.replace('%LEN%', String(o.data.length)) + '\nstream\n')); push(o.data); push(enc('\nendstream\nendobj\n')); }
  });
  var x = pos, xr = 'xref\n0 ' + (objs.length + 1) + '\n0000000000 65535 f \n';
  offs.forEach(function (o) { xr += String(o).padStart(10, '0') + ' 00000 n \n'; });
  push(enc(xr + 'trailer\n<< /Size ' + (objs.length + 1) + ' /Root 1 0 R /Info ' + objs.length + ' 0 R >>\nstartxref\n' + x + '\n%%EOF\n'));
  return new Blob(parts, { type: 'application/pdf' });
}
function pdfDate(d) {
  var p = function (v) { return String(v).padStart(2, '0'); };
  return 'D:' + d.getUTCFullYear() + p(d.getUTCMonth() + 1) + p(d.getUTCDate()) + p(d.getUTCHours()) + p(d.getUTCMinutes()) + p(d.getUTCSeconds()) + 'Z';
}

function build(spec) {
  var sp = Object.assign({ bleed: 0.0625, border: 0.125, kissMargin: 0.125, radiusFrac: 0.06 }, spec || {});
  if (!(sp.w > 0) || !(sp.h > 0)) return Promise.reject(new Error('size'));
  var g = geometry(sp);
  return loadLogo().then(function (logo) {
    var cs = content(sp, g, logo);
    // 1 catalog, 2 pages, 3 page, 4 contents, 5 OCG, 6 Helvetica, 7 Helvetica-Bold, 8 ExtGState tint, 9 panel, 10 swatch,
    // 11 logo, 12 logo alpha (optional), last = Info
    var objs = [];
    objs[0] = '<< /Type /Catalog /Pages 2 0 R /PageMode /UseOC /OCProperties << /OCGs [5 0 R] /D << /Name (Ardor layers) /Order [5 0 R] /ON [5 0 R] /OFF [] /BaseState /ON >> >> >>';
    objs[1] = '<< /Type /Pages /Kids [3 0 R] /Count 1 >>';
    var media = '[0 0 ' + n(g.PW) + ' ' + n(g.PH) + ']', trim = '[' + n(g.cut.x) + ' ' + n(g.cut.y) + ' ' + n(g.cut.x + g.cut.w) + ' ' + n(g.cut.y + g.cut.h) + ']';
    var xo = logo ? ' /XObject << /ArdorLogo 11 0 R >>' : '';
    objs[2] = '<< /Type /Page /Parent 2 0 R /MediaBox ' + media + ' /CropBox ' + media + ' /BleedBox ' + media + ' /TrimBox ' + trim +
      ' /Resources << /Font << /F1 6 0 R /F2 7 0 R >> /ExtGState << /GSTint 8 0 R /GSPanel 9 0 R /GSSwatch 10 0 R >>' + xo +
      ' /Properties << /ArdorGuides 5 0 R >> /ProcSet [/PDF /Text /ImageC] >> /Contents 4 0 R >>';
    objs[3] = { dict: '<< /Length %LEN% >>', data: enc(cs) };
    objs[4] = '<< /Type /OCG /Name ' + pdfStr(LAYER) + ' /Intent [/View /Design] /Usage << /CreatorInfo << /Creator (Ardor Printing) /Subtype /Artwork >> /View << /ViewState /ON >> /Print << /PrintState /ON >> /Export << /ExportState /ON >> >> >>';
    objs[5] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';
    objs[6] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';
    objs[7] = '<< /Type /ExtGState /ca 0.2 /CA 0.2 >>';
    objs[8] = '<< /Type /ExtGState /ca 0.86 /CA 0.86 >>';
    objs[9] = '<< /Type /ExtGState /ca 0.35 /CA 0.35 >>';
    if (logo) {
      objs[10] = { dict: '<< /Type /XObject /Subtype /Image /Width ' + logo.w + ' /Height ' + logo.h + ' /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode' + (logo.alpha ? ' /SMask 12 0 R' : '') + ' /Length %LEN% >>', data: logo.rgb };
      if (logo.alpha) objs[11] = { dict: '<< /Type /XObject /Subtype /Image /Width ' + logo.w + ' /Height ' + logo.h + ' /ColorSpace /DeviceGray /BitsPerComponent 8 /Filter /FlateDecode /Length %LEN% >>', data: logo.alpha };
    }
    var title = 'Ardor Printing sticker template - ' + inch(sp.w) + ' x ' + inch(sp.h) + ' in ' + shapeName(sp) + ' - Front';
    objs.push('<< /Title ' + pdfStr(title) + ' /Author (Ardor Printing) /Subject (Print template for ardorprinting.com - hide the "' + LAYER + '" layer before saving) /Creator (Ardor Printing sticker builder - ardorprinting.com) /Producer (Ardor Printing) /CreationDate (' + pdfDate(new Date()) + ') >>');
    for (var i = 0; i < objs.length; i++) if (objs[i] === undefined) objs[i] = '<< >>';
    return writePdf(objs);
  });
}

/* ---------- finished-template PDF upload -> image of page 1 with the guides layer hidden ---------- */
var libP = null;
function pdfjs() {
  if (!libP) {
    if (!CONF.pdfjs) return Promise.reject(new Error('no pdf reader'));
    // Shopify's asset minifier turns a literal dynamic import into require(), so the module is loaded through Function.
    libP = new Function('u', 'return import(u)')(CONF.pdfjs).then(function (lib) {
      if (CONF.worker && lib.GlobalWorkerOptions && !lib.GlobalWorkerOptions.workerSrc) lib.GlobalWorkerOptions.workerSrc = CONF.worker;
      return lib;
    }).catch(function (e) { libP = null; throw e; });
  }
  return libP;
}
function readPdf(file, pageW, pageH) {
  var doc = null, task = null;
  function done() { try { if (task && task.destroy) task.destroy(); else if (doc && doc.destroy) doc.destroy(); } catch (e) {} }
  return Promise.all([pdfjs(), file.arrayBuffer()]).then(function (r) {
    task = r[0].getDocument({ data: new Uint8Array(r[1]), isEvalSupported: false });
    return task.promise;
  }).then(function (d) {
    doc = d;
    return doc.getPage(1);
  }).then(function (page) {
    var v1 = page.getViewport({ scale: 1 }), wIn = v1.width / 72, hIn = v1.height / 72;
    if (Math.abs(wIn / pageW - 1) > 0.01 || Math.abs(hIn / pageH - 1) > 0.01) return null;
    var dpi = Math.min(300, Math.floor(Math.sqrt(16e6 / (wIn * hIn))), Math.floor(8000 / Math.max(wIn, hIn)));
    var vp = page.getViewport({ scale: dpi / 72 });
    var cv = document.createElement('canvas'); cv.width = Math.round(vp.width); cv.height = Math.round(vp.height);
    return doc.getOptionalContentConfig().then(function (cfg) {
      var hid = 0;
      try {
        var it = typeof cfg[Symbol.iterator] === 'function' ? Array.from(cfg) : Object.entries(cfg.getGroups ? cfg.getGroups() || {} : {});
        it.forEach(function (pair) { var grp = pair[1]; if (grp && /^ardor guides/i.test(grp.name || '')) { cfg.setVisibility(pair[0], false); hid++; } });
      } catch (e) {}
      return page.render({ canvas: cv, canvasContext: cv.getContext('2d'), viewport: vp, background: 'rgba(0,0,0,0)', optionalContentConfigPromise: Promise.resolve(cfg) }).promise.then(function () {
        return new Promise(function (res) { cv.toBlob(res, 'image/png'); });
      }).then(function (blob) {
        if (!blob) return null;
        return { url: URL.createObjectURL(blob), w: cv.width, h: cv.height, dpi: dpi, guidesHidden: hid };
      });
    });
  }).then(function (res) {
    done();
    return res;
  }, function (e) {
    done();
    throw e;
  });
}

window.ArdorStickerTemplate = { build: build, fileName: fileName, shapeName: shapeName, geometry: geometry, readPdf: readPdf, layerName: LAYER };
})();
